import { useEffect } from 'react';

import { useCommandPaletteStore } from '@/shared/store/commandPalette';
import { useShortcutSheetStore } from '@/shared/store/shortcutSheet';

/**
 * Whether the palette is open, and the key that opens it (spec 077 FR-001, FR-002).
 *
 * Bound on `window` rather than on any element, for the reason `useUndoShortcuts` gives
 * for doing the same: the action is about the application, not about whichever element
 * happens to hold focus.
 *
 * **There is deliberately no text-entry guard, and that is the opposite of undo's rule.**
 * A reader typing a prompt who presses ⌘K still means "open the palette" — nothing in a
 * text field owns that combination, and no native behaviour is taken away. Suppressing it
 * would make the palette unreachable from exactly the long-form surfaces where wanting to
 * go somewhere else is most likely.
 *
 * The one exception is the markdown editor (spec 145), where ⌘K adds a link. It is not a
 * guard here: the editor stops the key on its own text box, so this never hears it.
 *
 * Undo suppresses inside a text field because a textarea owns its own undo stack. `?`
 * suppresses because it is a character. Three keys, three guards, each following from
 * what the key means — see `specs/077-command-palette/contracts/shortcuts.md`, which
 * exists to stop a future contributor unifying them.
 */
export function useCommandPalette() {
  const open = useCommandPaletteStore((state) => state.open);
  const setOpen = useCommandPaletteStore((state) => state.setOpen);
  const toggle = useCommandPaletteStore((state) => state.toggle);

  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      if (event.key.toLowerCase() !== 'k') return;

      // Browsers bind ⌘K to the address bar in some configurations; taking it is the
      // point of the shortcut, and the reader can still reach the bar by other means.
      event.preventDefault();
      toggle();
    };

    window.addEventListener('keydown', handle);
    return () => {
      window.removeEventListener('keydown', handle);
    };
  }, [toggle]);

  return { open, setOpen };
}

/**
 * Whether the shortcut sheet is open, and the key that opens it (FR-018, FR-020).
 *
 * `?` **is** guarded against text entry, unlike the palette's key. This is not a
 * collision guard; it is the difference between a shortcut and a typo. Without it nobody
 * could write a question in any field in the product.
 */
export function useShortcutSheet() {
  const open = useShortcutSheetStore((state) => state.open);
  const setOpen = useShortcutSheetStore((state) => state.setOpen);

  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (isTextEntry(event.target)) return;

      event.preventDefault();
      setOpen(true);
    };

    window.addEventListener('keydown', handle);
    return () => {
      window.removeEventListener('keydown', handle);
    };
  }, [setOpen]);

  return { open, setOpen };
}

/** Whether the event came from somewhere that owns its own text. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag !== 'INPUT') return false;

  const type = (target as HTMLInputElement).type;
  return type !== 'checkbox' && type !== 'radio' && type !== 'button';
}
